/**
 * [필터] 묶음 — 자주 쓰는 거르개(검색·형식·상태)만 머리에 두고 나머지를 한 단추 뒤로 접는다(점진적 공개).
 *
 * 머리줄에 고르개가 여덟 개 늘어서면 무엇이 기본인지 보이지 않고, 좁은 화면에서는 두세 줄로 꺾인다.
 * 접어도 **걸려 있다는 것은 보여야 한다** — 단추에 걸린 개수를 적고 색을 바꾼다(`activeCount`).
 * 걸린 것을 모르고 "목록에 없다" 고 읽는 것이 거르개를 접을 때 가장 흔한 사고다.
 */
import { SlidersHorizontal } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { placeMenu } from './MoreMenu.js';

/** 단추 글자 — 걸린 것이 있으면 개수를 붙인다. */
export function filterButtonLabel(label: string, activeCount: number): string {
  return activeCount > 0 ? `${label} ${activeCount}` : label;
}

export function FilterPopover({
  label = '필터',
  activeCount,
  onReset,
  children,
}: {
  label?: string;
  /** 지금 걸려 있는 (이 묶음 안의) 거르개 수. */
  activeCount: number;
  /** 이 묶음의 거르개를 모두 푼다. 걸린 것이 있을 때만 [모두 해제] 가 선다. */
  onReset?: () => void;
  children: ReactNode;
}): ReactNode {
  const id = useId();
  const anchor = useRef<HTMLButtonElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    setPosition(null);
    if (refocus) anchor.current?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!open || !anchor.current || !box.current) return;
    const a = anchor.current.getBoundingClientRect();
    const b = box.current.getBoundingClientRect();
    const next = placeMenu(a, { width: b.width, height: b.height }, { width: window.innerWidth, height: window.innerHeight });
    setPosition((current) => (current && current.top === next.top && current.left === next.left ? current : next));
  });

  useEffect(() => {
    if (!open) return;
    box.current?.querySelector<HTMLElement>('select, input, button')?.focus();
    // 안에서 띄운 달력(`DateField`)은 문서 끝에 붙지만 이 창의 일부다(`data-float-layer`).
    const inside = (target: EventTarget | null): boolean =>
      target instanceof Node &&
      (anchor.current?.contains(target) === true ||
        box.current?.contains(target) === true ||
        (target instanceof Element && target.closest('[data-float-layer]') !== null));
    const onPointer = (event: PointerEvent): void => {
      if (!inside(event.target)) close(false);
    };
    // 안의 칸이 Esc 를 먼저 썼으면(달력만 닫기) 이 창은 그대로 둔다.
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !event.defaultPrevented) close(true);
    };
    // 날짜 고르개처럼 안에서 생기는 스크롤은 닫을 일이 아니다.
    const onScroll = (event: Event): void => {
      if (!inside(event.target)) close(false);
    };
    const onResize = (): void => close(false);
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('pointerdown', onPointer, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, close]);

  const text = filterButtonLabel(label, activeCount);
  return (
    <>
      <button
        ref={anchor}
        type="button"
        className={`btn filter-pop__trigger${activeCount > 0 ? ' is-active' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-label={activeCount > 0 ? `${label} — ${activeCount}개 걸림` : label}
        onClick={() => (open ? close(false) : setOpen(true))}
      >
        <SlidersHorizontal className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
        {text}
      </button>
      {open
        ? createPortal(
            <div
              ref={box}
              id={id}
              role="dialog"
              aria-label={label}
              className="filter-pop"
              style={position ?? { top: -9999, left: -9999 }}
            >
              <div className="filter-pop__body">{children}</div>
              {onReset && activeCount > 0 ? (
                <div className="filter-pop__foot">
                  <button type="button" className="link" onClick={onReset}>
                    모두 해제
                  </button>
                </div>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

/** 묶음 안의 한 칸 — 이름 위, 고르개 아래. */
export function FilterField({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <label className="filter-pop__field">
      <span>{label}</span>
      {children}
    </label>
  );
}
